import { memo } from 'react';
import PropTypes from 'prop-types';
import Link from '@/components/component-library/Link';
import PlayPauseCTA from '@/components/component-library/Button/PlayPauseCTA';
import Text, { FONT_SIZE_160 } from '@/components/component-library/Text';
import { classes } from '@/helpers/styling';
import { truncateText } from '@/helpers/utility';
import GenreBadges from '@/components/component-library/GenreBadges';
import InfoIcon from '@/components/component-library/Icon/types/Info';
import TextLoadingBlock from '@/components/component-library/Text/TextLoadingBlock';
import { isShowNewlyReleased } from '@/helpers/common';
import BadgeNew from '@/components/component-library/BadgeNew';

const SlateInformation = ({
  loading,
  loadingPlayButton,
  hidePlayButton,
  title,
  genres,
  onClickPlay,
  onClickInfo,
  showHref,
  isPaused,
  seasonNumber,
  episodeNumber,
  isCurrentlyPlayingShow,
  description,
  releaseDate,
  hideDescription
}) => {
  const showDescription = !hideDescription;
  const showPlayButton = !!onClickPlay && !hidePlayButton;
  const moreInfoAvailable = (onClickInfo || showHref);

  return (
    <div
      className='slate-info-desktop'
      data-testid='slate-information'
    >
      <Link
        className={ classes('slate-title', { '--is-clickable': !loading && moreInfoAvailable }) }
        href={ showHref }
        isDisabled={ loading }
        onClick={ onClickInfo }
        style={ {
          flexDirection: 'column',
          alignItems: 'start'
        } }
      >
        { !!isShowNewlyReleased({ releaseDate }) && <BadgeNew /> }
        <Text
          bold
          className={ classes('title', {
            '--is-loading': loading,
            '--description-is-hidden': !showDescription
          }) }
          loading={ loading }
          size={ FONT_SIZE_160 }
          useHeaderFont
        >
          { title }
        </Text>
      </Link>
      <div
        className={ classes('slate-info-summary-desktop', { '--extra-bottom-margin': !showDescription }) }
      >
        <GenreBadges enableLinks genres={ genres } loading={ loading } />
      </div>
      { (loading ? showDescription : (showDescription && !!description)) && (
        <div className='description-wrapper'>
          { loading ? (
            <TextLoadingBlock lineCount={ 4 } />
          ) : (
            <Text>
              { truncateText(description, 250) }
            </Text>
          ) }
        </div>
      ) }
      <div className='slate-info-desktop-buttons'>
        { showPlayButton && (
          <PlayPauseCTA
            disabled={ loadingPlayButton }
            episodeNumber={ episodeNumber }
            isCurrentlyPlayingShow={ isCurrentlyPlayingShow }
            loading={ loadingPlayButton }
            onClick={ onClickPlay }
            paused={ isPaused }
            seasonNumber={ seasonNumber }
          />
        ) }
        { moreInfoAvailable && (
          <Link
            className='slate-information-info-icon'
            href={ showHref }
            onClick={ onClickInfo }
          >
            <InfoIcon />
          </Link>
        ) }
      </div>
    </div>
  );
};

SlateInformation.propTypes = {
  loading: PropTypes.bool,
  loadingPlayButton: PropTypes.bool,
  title: PropTypes.string,
  genres: PropTypes.arrayOf(PropTypes.object),
  onClickPlay: PropTypes.func.isRequired,
  hidePlayButton: PropTypes.bool,
  onClickInfo: PropTypes.func,
  isPaused: PropTypes.bool,
  seasonNumber: PropTypes.number,
  episodeNumber: PropTypes.number,
  isCurrentlyPlayingShow: PropTypes.bool,
  description: PropTypes.string,
  hideDescription: PropTypes.bool,
  showHref: PropTypes.string,
  releaseDate: PropTypes.string
};

SlateInformation.defaultProps = {
  loading: false,
  loadingPlayButton: false,
  title: '',
  genres: [],
  hidePlayButton: false,
  onClickInfo: undefined,
  isPaused: false,
  seasonNumber: null,
  episodeNumber: null,
  isCurrentlyPlayingShow: false,
  description: '',
  hideDescription: false,
  showHref: false,
  releaseDate: undefined
};

export default memo(SlateInformation);